'use strict';

/**
 * Presentational publishing-progress tracker. Production and showcase both feed this
 * view the same models produced by {@link buildPipelineProgressBundleFromSnapshot}.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Collapse,
	Skeleton,
	Stepper,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { Gray } from 'metabux/theme/colors';
import { PIPELINE_PROGRESS_UI_COPY } from './pipelineProgressCopy';
import { usePipelineProgress } from './usePipelineProgress';
import { PipelineProgressStep } from './PipelineProgressStep';
import { PipelineStageDetails } from './PipelineStageDetails';
import { CorePipelineProgressSummary } from './CorePipelineProgressSummary';
import { DiscoveryPanelsPipelineProgressSummary } from './DiscoveryPanelsPipelineProgressSummary';
import { KitsPipelineProgressSummary } from './KitsPipelineProgressSummary';
import { MergedPipelineProgressSummary } from './MergedPipelineProgressSummary';

const PANEL_MIN_HEIGHT = 132;

/**
 * @param {{
 *   kind: 'v7' | 'core' | 'discoveryPanels' | 'kits' | 'merged' | 'unsupported',
 *   model: import('./pipelineProgressModel').PipelineProgressModel,
 *   coreModel: import('./pipelineCoreProgressModel').CorePipelineProgressModel,
 *   discoveryModel: import('./pipelineCoreProgressModel').CorePipelineProgressModel,
 *   kitsModel: import('./pipelineCoreProgressModel').CorePipelineProgressModel,
 *   stages: import('./pipelineStatusTypes').PipelineStatusStage[],
 *   facts?: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   definition?: import('./pipelineStatusTypes').PipelineStatusGraphDefinition | null,
 *   projectId?: string,
 *   isLoading?: boolean,
 *   error?: unknown,
 * }} props
 */
export function PipelineProgressSummaryView({
	kind,
	model,
	coreModel,
	discoveryModel,
	kitsModel,
	stages,
	facts = [],
	definition = null,
	projectId,
	isLoading = false,
	error,
}) {
	const theme = useTheme();
	const narrow = useMediaQuery(theme.breakpoints.down('md'));
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedStepId, setSelectedStepId] = useState(/** @type {string | null} */ (null));

	useEffect(() => {
		setSelectedStepId(null);
	}, [model.currentStepId]);

	const effectiveSelectedId = selectedStepId ?? model.currentStepId ?? model.steps[0]?.id;
	const selectedStep = useMemo(
		() => model.steps.find((s) => s.id === effectiveSelectedId),
		[model.steps, effectiveSelectedId],
	);

	const onSelectStep = (stepId) => {
		setSelectedStepId(stepId);
		setDetailsOpen(true);
	};

	if (error) {
		const diagnostic =
			error instanceof Error && String(error.message).includes('[pipelineStatus]')
				? error.message
				: 'Publishing progress could not be loaded.';
		return (
			<Alert severity="warning" sx={{ mt: 1 }} data-testid="pipeline-progress-error">
				{diagnostic}
			</Alert>
		);
	}

	if (isLoading) {
		return (
			<Box sx={{ minHeight: PANEL_MIN_HEIGHT, mt: 1.5 }} data-testid="pipeline-progress-loading">
				<Skeleton variant="rounded" height={PANEL_MIN_HEIGHT - 12} />
			</Box>
		);
	}

	if (kind === 'discoveryPanels') {
		return <DiscoveryPanelsPipelineProgressSummary model={discoveryModel} facts={facts} definition={definition} projectId={projectId} />;
	}

	if (kind === 'core') {
		return <CorePipelineProgressSummary model={coreModel} facts={facts} definition={definition} projectId={projectId} />;
	}

	if (kind === 'kits') {
		return <KitsPipelineProgressSummary model={kitsModel} facts={facts} definition={definition} projectId={projectId} />;
	}

	if (kind === 'merged') {
		return <MergedPipelineProgressSummary model={model} stages={stages} definition={definition} />;
	}

	const inactive = model.state === 'unavailable';

	const activeStepIndex = Math.max(
		0,
		model.steps.findIndex((s) => s.id === model.currentStepId),
	);

	return (
		<Box
			component="section"
			aria-label={PIPELINE_PROGRESS_UI_COPY.panelTitle}
			className="PipelineProgressSummary"
			data-testid="pipeline-progress-summary"
			sx={{
				mt: 1.5,
				p: 2,
				border: 1,
				borderColor: 'divider',
				borderRadius: 1,
				bgcolor: 'background.paper',
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, position: 'relative', zIndex: 1 }}>
				<Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: Gray[600], flexGrow: 1, fontSize: '1rem' }}>
					{PIPELINE_PROGRESS_UI_COPY.panelTitle}
				</Typography>
				{!inactive && (
					<Button
						size="small"
						onClick={() => setDetailsOpen((v) => !v)}
						data-testid="pipeline-progress-details-toggle"
						aria-label={detailsOpen ? PIPELINE_PROGRESS_UI_COPY.hideDetails : PIPELINE_PROGRESS_UI_COPY.viewDetails}
					>
						{detailsOpen ? PIPELINE_PROGRESS_UI_COPY.hideDetails : PIPELINE_PROGRESS_UI_COPY.viewDetails}
					</Button>
				)}
			</Box>

			<Stepper
				nonLinear
				activeStep={inactive ? -1 : activeStepIndex}
				orientation={narrow ? 'vertical' : 'horizontal'}
				alternativeLabel={!narrow}
				sx={{
					mb: 1.5,
					'& .MuiStepLabel-iconContainer svg': {
						width: 22,
						height: 22,
						border: 'none',
						borderRadius: 0,
						color: 'inherit',
					},
					'&.MuiStepper-horizontal .MuiStepConnector-root': {
						top: '11px',
						left: 'calc(-50% + 18px)',
						right: 'calc(50% + 18px)',
					},
					'&.MuiStepper-vertical .MuiStepConnector-root': { marginLeft: '11px' },
					'&.MuiStepper-vertical .MuiStepConnector-line': {
						border: 'none',
						borderLeft: '2px solid',
						borderColor: 'grey.300',
						minHeight: 16,
					},
					'&.MuiStepper-vertical .Mui-active .MuiStepConnector-line, &.MuiStepper-vertical .Mui-completed .MuiStepConnector-line': {
						border: 'none',
						borderLeft: '2px solid #2a85ff',
					},
					'& .MuiStepLabel-label, & .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
						color: 'text.primary',
						fontSize: '0.8125rem',
					},
				}}
			>
				{model.steps.map((step) => (
					<PipelineProgressStep
						key={step.id}
						step={step}
						isCurrent={!inactive && step.id === model.currentStepId}
						onSelect={onSelectStep}
						inactive={inactive}
					/>
				))}
			</Stepper>

			<Typography
				variant="body2"
				sx={{ color: model.state === 'failed' ? 'error.main' : inactive ? 'text.secondary' : Gray[800] }}
				aria-live="polite"
				role={model.state === 'failed' ? 'alert' : undefined}
				data-testid={inactive ? 'pipeline-progress-unavailable' : 'pipeline-progress-message'}
			>
				{model.message}
				{!inactive && model.durationLabel ? ` · ${model.durationLabel}` : ''}
			</Typography>

			{!inactive && (
				<Collapse in={detailsOpen} unmountOnExit>
					<PipelineStageDetails step={selectedStep} stages={stages} />
				</Collapse>
			)}
		</Box>
	);
}

/**
 * Production project-page tracker: RTK → shared builders → {@link PipelineProgressSummaryView}.
 * @param {{ projectId: string | undefined; projectPipeline: unknown; hasDiscoveryPanels?: boolean }} props
 */
export function PipelineProgressSummary({ projectId, projectPipeline, hasDiscoveryPanels }) {
	const progress = usePipelineProgress(projectId, projectPipeline, { hasDiscoveryPanels });
	return (
		<PipelineProgressSummaryView
			kind={progress.kind}
			model={progress.model}
			coreModel={progress.coreModel}
			discoveryModel={progress.discoveryModel}
			kitsModel={progress.kitsModel}
			stages={progress.stages}
			definition={progress.definition}
			facts={progress.facts}
			projectId={projectId}
			isLoading={progress.isLoading}
			error={progress.error}
		/>
	);
}
